export const ALL_LOCATIONS_SENTINEL = "ALL";

/**
 * @typedef {Object} Access
 * @property {boolean} allLocations
 * @property {string[]} regionIds
 * @property {string[]} areaIds
 * @property {string[]} businessIds
 */

/**
 * @typedef {Object} BusinessLike
 * @property {string} businessId
 * @property {string} [regionId]
 */

/**
 * @typedef {Object} RegionLike
 * @property {string} id
 * @property {boolean} [isArea]
 * @property {string} [parentRegion]
 */

export function emptyAccess() {
  return { allLocations: false, regionIds: [], areaIds: [], businessIds: [] };
}

export function isAllLocations(resolved) {
  return resolved === ALL_LOCATIONS_SENTINEL;
}

/**
 * Roles that always see every location in the account, regardless of any
 * explicit region/area/business grants. FE mirror of the backend
 * `roleSeesAllLocations` so the edit form can lock the access panel exactly
 * when the backend would force `allLocations: true` anyway.
 *
 * @param {string} [role]
 * @returns {boolean}
 */
export function roleSeesAllLocations(role) {
  return role === "admin" || role === "super-admin" || role === "analyst";
}

/**
 * Expands `regionIds` into the areas beneath them (per the live region
 * tree), returning a fresh Set covering both the explicit `areaIds` and the
 * cascaded ones. Every FE tree-pruning site (Charts, Dashboard, the access
 * assignment panel) needs this same region -> area cascade to keep its view
 * consistent with what `resolveAccessibleBusinessIds` actually grants — pull
 * from here instead of re-deriving it so the cascade rule only lives once.
 *
 * @param {string[]} [areaIds]
 * @param {string[]} [regionIds]
 * @param {RegionLike[]} regions
 * @returns {Set<string>}
 */
export function expandAreaIds(areaIds, regionIds, regions) {
  const result = new Set(areaIds || []);
  const regionIdSet = new Set(regionIds || []);
  if (regionIdSet.size > 0) {
    regions.forEach(r => {
      if (r.isArea && r.parentRegion && regionIdSet.has(r.parentRegion)) {
        result.add(r.id);
      }
    });
  }
  return result;
}

/**
 * Pure resolver. Returns ALL_LOCATIONS_SENTINEL when the user has access to
 * every location in the account; otherwise a Set of accessible business ids.
 *
 * A location is accessible iff:
 *   access.allLocations === true, OR
 *   business.businessId is in access.businessIds, OR
 *   business.regionId (an area doc id) is in access.areaIds, OR
 *   the parent region of business.regionId is in access.regionIds.
 *
 * Granting a region implicitly grants every area (and therefore every
 * business) under that region.
 *
 * @param {Access} [access]
 * @param {BusinessLike[]} businesses
 * @param {RegionLike[]} regions
 * @returns {Set<string>|string}
 */
export function resolveAccessibleBusinessIds(access, businesses, regions) {
  if (access && access.allLocations) {
    return ALL_LOCATIONS_SENTINEL;
  }

  const regionIds = new Set(access ? access.regionIds : []);
  const areaIds = expandAreaIds(access ? access.areaIds : [], access ? access.regionIds : [], regions);
  const explicitBusinessIds = new Set(access ? access.businessIds : []);

  const areaToParent = new Map();
  for (const r of regions) {
    if (r.isArea && r.parentRegion) {
      areaToParent.set(r.id, r.parentRegion);
    }
  }

  const result = new Set();
  for (const b of businesses) {
    if (!b.businessId) {
      continue;
    }
    if (explicitBusinessIds.has(b.businessId)) {
      result.add(b.businessId);
      continue;
    }
    const areaId = b.regionId;
    if (!areaId) {
      continue;
    }
    if (areaIds.has(areaId)) {
      result.add(b.businessId);
      continue;
    }
    const parent = areaToParent.get(areaId);
    if (parent && regionIds.has(parent)) {
      result.add(b.businessId);
    }
  }
  return result;
}

/**
 * @param {Access} [access]
 * @param {BusinessLike} business
 * @param {RegionLike[]} regions
 * @returns {boolean}
 */
export function userHasAccessToBusiness(access, business, regions) {
  if (access && access.allLocations) {
    return true;
  }
  const resolved = resolveAccessibleBusinessIds(access, [business], regions);
  if (resolved === ALL_LOCATIONS_SENTINEL) {
    return true;
  }
  return resolved.has(business.businessId);
}

/**
 * @param {Access} [access]
 * @param {BusinessLike[]} businesses
 * @param {RegionLike[]} regions
 * @returns {BusinessLike[]}
 */
export function filterBusinessesByAccess(access, businesses, regions) {
  const resolved = resolveAccessibleBusinessIds(access, businesses, regions);
  if (resolved === ALL_LOCATIONS_SENTINEL) {
    return businesses;
  }
  return businesses.filter(b => resolved.has(b.businessId));
}

/**
 * Compute the role-seeded default `access` for a newly invited/created user.
 * FE mirror of the backend `getRoleDefaultAccess` so the admin form can
 * pre-populate the assignment panel exactly as the backend would.
 *
 *   admin / super-admin / analyst  -> allLocations: true
 *   regional-manager                -> regionIds: [parentRegion]
 *   area-manager                    -> areaIds: [the area of primary location]
 *   user / manager / unknown        -> businessIds: [primary location]
 *
 * @param {string} role
 * @param {string} businessId
 * @param {BusinessLike[]} businesses
 * @param {RegionLike[]} regions
 * @returns {Access}
 */
export function getRoleDefaultAccess(role, businessId, businesses, regions) {
  const access = emptyAccess();
  if (roleSeesAllLocations(role)) {
    access.allLocations = true;
    return access;
  }
  if (!businessId) {
    return access;
  }
  const primary = businesses.find(b => b.businessId === businessId);
  const areaId = primary && primary.regionId ? primary.regionId : undefined;
  if (role === "regional-manager") {
    const area = regions.find(r => r.id === areaId && r.isArea);
    const parent = area && area.parentRegion ? area.parentRegion : undefined;
    if (parent) {
      access.regionIds = [parent];
    } else if (areaId) {
      access.areaIds = [areaId];
    } else {
      access.businessIds = [businessId];
    }
    return access;
  }
  if (role === "area-manager") {
    if (areaId) {
      access.areaIds = [areaId];
    } else {
      access.businessIds = [businessId];
    }
    return access;
  }
  access.businessIds = [businessId];
  return access;
}
